import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { setDoc, deleteDoc } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { sortTemplates, templateDocument } from '../domain/noteTemplates';
import { useCollectionWindow } from './useCollectionWindow';

// Lesson note templates: a small managed list, shaped like categories and
// locations in useCategories.jsx.
//
// The simpler half of that pattern. Categories carry a built-in list that is
// materialised into documents on first write, guarded by a `hasRead` ref
// because "nothing stored" there means two different things. There is no
// built-in template: an empty collection is an empty collection, and a save
// writes exactly the one document it was asked to.
//
// Random ids, not derived ones. Two devices offline, each adding "Scales" to
// the list, add two templates; that is the "genuinely separate" case
// domain/freeId.js is for. Nothing else points at a template, so a
// duplicate is a duplicate row in a list and not a double charge.

const NoteTemplatesContext = createContext(null);

// The same bound the other studio-level lists use; the picker is a flat menu.
const TRACK_LIMIT = 200;

export function NoteTemplatesProvider({ children }) {
  const { user } = useAuth();

  // The note-templates window, from whichever read path serves this session
  // (#207 U5). No `withMetadata`: nothing here reads a pending write.
  // `loaded` is the hook's `hasRead` — have we read *this studio's* window —
  // and is false again on sign-out.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.noteTemplates,
    limit: TRACK_LIMIT,
  });
  const stored = read.docs;
  const loaded = read.hasRead;

  const value = useMemo(() => ({
    loaded,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    templates: sortTemplates(stored),

    // Save is create *and* rename: passing an id edits that template in place,
    // which the manager needs — a template body is long enough that fixing a
    // typo by deleting and retyping it is not a real option, unlike a category
    // whose whole content is a word and a colour.
    saveTemplate: async ({ id, label, body }) => {
      if (!user) return null;
      // The rules refuse an empty label or body outright. Refusing here as well
      // (`templateDocument` answers null) keeps a mis-wired form from producing
      // a permission error, which reads like a broken account rather than an
      // empty field.
      const doc = templateDocument({ label, body }, serverTimestamp());
      if (!doc) return null;
      const tid = id || freeId('tpl');
      await setDoc(docRef(user.uid, COLLECTIONS.noteTemplates, tid), doc, { merge: true });
      return tid;
    },

    removeTemplate: async (id) => {
      if (!user || !id) return;
      await deleteDoc(docRef(user.uid, COLLECTIONS.noteTemplates, id));
    },
  }), [stored, loaded, read.failed, user]);

  return <NoteTemplatesContext.Provider value={value}>{children}</NoteTemplatesContext.Provider>;
}

export function useNoteTemplates() {
  const ctx = useContext(NoteTemplatesContext);
  if (!ctx) throw new Error('useNoteTemplates must be used within a NoteTemplatesProvider');
  return ctx;
}
